/* Blog posts, newest first. Each body block renders as a heading, paragraph, or bullet list. */
export const posts = [
  {
    slug: "workloading-part-two",
    title: "Workloading part two",
    subtitle:
      "Building Profiles and Heat Maps: Assigning Cleaning Resources Based on How a Facility Is Actually Used",
    date: "2026-09-13",
    excerpt:
      "Two buildings of the same size can need completely different cleaning strategies. A building profile and a heat map show you where the work actually is—and when.",
    body: [
      {
        type: "p",
        text:
          "A 100,000-square-foot office building with limited daily traffic may require a very different cleaning strategy than a 100,000-square-foot academic building with thousands of students, employees, and visitors moving through it every day."
      },
      {
        type: "p",
        text:
          "This is why workloading should go beyond simply dividing square footage by a production rate."
      },
      { type: "h2", text: "Understanding the Building Profile" },
      { type: "p", text: "Every building has its own unique profile." },
      {
        type: "p",
        text:
          "Before determining staffing levels or recommending a cleaning program, it is important to understand what happens inside the facility."
      },
      { type: "p", text: "A building profile may include:" },
      {
        type: "ul",
        items: [
          "The type of facility",
          "Number of employees and occupants",
          "Number of daily visitors",
          "Operating hours",
          "Special events and activities",
          "Types of rooms and spaces",
          "Restrooms and other high-service areas",
          "Public spaces and high-visibility locations",
          "Service expectations"
        ]
      },
      {
        type: "p",
        text:
          "A university classroom building, medical office, library, administrative office, and community center may have similar square footage, but they should not necessarily be cleaned the same way."
      },
      { type: "p", text: "The function of the building matters." },
      { type: "h2", text: "Heat Maps Can Show the Busiest Times" },
      {
        type: "p",
        text:
          "One of the most valuable parts of analyzing a facility is understanding when people are actually using the building."
      },
      {
        type: "p",
        text:
          "A heat map can help identify the busiest areas and busiest hours throughout the day."
      },
      {
        type: "p",
        text:
          "For example, an entrance may experience heavy traffic between 7:30 a.m. and 9:00 a.m. A restroom may have its highest usage during class changes or employee breaks. A lobby may become extremely busy before an event and remain relatively quiet for the rest of the day."
      },
      { type: "p", text: "This information can help answer important questions:" },
      {
        type: "ul",
        items: [
          "When are the busiest hours?",
          "Which areas receive the most traffic?",
          "Which spaces require multiple services during the day?",
          "When can cleaning be performed with the least disruption?",
          "Are employees assigned to the areas where they are needed most?"
        ]
      },
      {
        type: "p",
        text:
          "A building may not need the same level of service in every area throughout the entire day."
      },
      {
        type: "p",
        text:
          "Understanding the heat map allows cleaning resources to be strategically assigned based on actual activity."
      }
    ]
  },
  {
    slug: "workloading-part-one",
    title: "Workloading part one",
    subtitle:
      "Workloading Is More Than Square Footage: Understanding the Building Before Choosing a Cleaning Program",
    date: "2026-08-23",
    excerpt:
      "Gross and cleanable square footage are a starting point, not an answer. Before you pick a cleaning program, you have to understand how the building actually functions.",
    body: [
      {
        type: "p",
        text:
          "When organizations evaluate housekeeping staffing, one of the first questions asked is, “How many square feet does the building have?” While gross square footage and cleanable square footage are important starting points, they only tell part of the story."
      },
      {
        type: "p",
        text:
          "An effective workload analysis goes much deeper. The goal isn’t simply to assign cleaning tasks based on size—it’s to understand how the building actually functions so you can select the cleaning program that delivers the best results."
      },
      { type: "h2", text: "Square Footage Is Only the Beginning" },
      {
        type: "p",
        text:
          "Gross square footage helps determine the overall size of a facility, while cleanable square footage identifies the areas that require routine cleaning. These measurements provide a baseline for estimating labor requirements, but they cannot accurately predict the daily demands placed on a housekeeping team."
      },
      {
        type: "p",
        text:
          "Two buildings with the same cleanable square footage can require completely different staffing levels because of how they are used."
      }
    ]
  }
];

export function getPost(slug) {
  return posts.find((post) => post.slug === slug);
}

export function formatDate(iso) {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC"
  });
}
